// src/composables/useExposureObserver.js

import { ref, onMounted, onUnmounted, nextTick } from 'vue';

/**
 * Vue 3 Composable: 监测目标元素是否进入视口并达到指定曝光条件。
 * 基于 Intersection Observer API 实现。
 *
 * @param {Ref<HTMLElement | null>} targetRef - 一个 Vue Ref，必须绑定到要观察的 DOM 元素。
 * @param {Object} [options={}] - IntersectionObserver 的配置选项。
 * @param {HTMLElement | null} [options.root=null] - 视口元素。默认为浏览器视口 (null)。
 * @param {string} [options.rootMargin='0px'] - 根元素的外边距，如 '10px 20px 30px 40px' (上右下左)。
 * @param {number | number[]} [options.threshold=0.1] - 触发回调的交叉比例，0 到 1 之间的数字或数组。
 * @param {boolean} [once=false] - 曝光回调是否只触发一次。
 * @returns {Object} - 包含响应式曝光状态和控制方法的对象。
 * @property {Ref<boolean>} isIntersecting - 目标元素是否正在与根元素交叉。
 * @property {Ref<boolean>} isExposed - 目标元素是否已经达到曝光阈值 (isIntersecting && intersectionRatio >= threshold)。
 * @property {Ref<IntersectionObserverEntry | null>} intersectionEntry - 最新的 IntersectionObserverEntry 对象。
 * @property {Function} reobserve - 手动重新初始化观察者的方法，用于动态内容或重置状态。
 */
export function useExposureObserver(
  targetRef, // 必须是一个 `ref` 对象，绑定到你要观察的 DOM 元素
  options = {},
  once = false // 默认不只触发一次
) {
  // 从 options 中解构配置，并设置默认值
  const { root = null, rootMargin = '0px', threshold = 0.1 } = options;

  // 定义响应式状态
  const isIntersecting = ref(false);       // 元素当前是否正在与根元素交叉
  const isExposed = ref(false);           // 元素是否已经达到“曝光到点”状态
  const intersectionEntry = ref(null);    // 存储最新的 IntersectionObserverEntry 对象

  let observer = null; // IntersectionObserver 实例

  // Intersection Observer 的回调函数
  const handleIntersection = (entries) => {
    entries.forEach((entry) => {
      isIntersecting.value = entry.isIntersecting; // 更新当前交叉状态
      intersectionEntry.value = entry;             // 存储当前的交叉信息

      // 判断是否达到曝光条件：元素进入视口且交叉比例达到阈值
      if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
        // 只有当之前未曝光过，或者允许重复触发时，才将 isExposed 设为 true
        if (!isExposed.value || !once) {
          isExposed.value = true; // 标记为已曝光
          // console.log(`元素曝光了！交叉比例: ${entry.intersectionRatio.toFixed(2)}`); // 调试信息

          // 如果设置了只触发一次，则停止观察
          if (once && observer) {
            observer.unobserve(entry.target); // 停止观察当前目标
            observer.disconnect();            // 完全断开观察器
            observer = null;                  // 清除 observer 引用
          }
        }
      } else if (!entry.isIntersecting && isExposed.value && !once) {
        // 如果元素离开了视口，并且不是一次性触发模式，重置 isExposed 状态
        isExposed.value = false;
      }
    });
  };

  // 组件挂载时，初始化 Intersection Observer
  onMounted(() => {
    // 使用 nextTick 确保 targetRef.value 已经绑定到实际 DOM 元素
    nextTick(() => {
      if (targetRef.value) {
        observer = new IntersectionObserver(handleIntersection, { root, rootMargin, threshold });
        observer.observe(targetRef.value); // 开始观察目标元素
      } else {
        console.warn('useExposureObserver: 目标元素 ref 为空。请确保它已绑定到模板中的 DOM 元素。');
      }
    });
  });

  // 组件卸载时，停止 Intersection Observer，防止内存泄漏
  onUnmounted(() => {
    if (observer) {
      observer.disconnect(); // 停止观察所有目标
      observer = null;       // 清除 observer 引用
    }
  });

  /**
   * 手动重新初始化观察者。
   * 当目标元素内容或大小发生变化，或需要重置曝光状态并重新检测时调用。
   */
  const reobserve = () => {
    if (observer) {
      observer.disconnect(); // 先断开旧的观察
      observer = null;
    }
    // 重置曝光状态
    isExposed.value = false;
    isIntersecting.value = false;
    intersectionEntry.value = null;

    // 在下一个 DOM 更新周期后重新创建并观察
    nextTick(() => {
      if (targetRef.value) {
        observer = new IntersectionObserver(handleIntersection, { root, rootMargin, threshold });
        observer.observe(targetRef.value);
      }
    });
  };

  // 返回响应式状态和方法，供组件使用
  return {
    isIntersecting,
    isExposed,
    intersectionEntry,
    reobserve,
  };
}